import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import {
  Target, Users, Zap, Globe, TrendingUp, Award, Shield, Heart,
  Lightbulb, Rocket, Linkedin, Mail, Check,
} from 'lucide-react';
import { toast } from 'sonner';

// About Us — Swiss Minimalist + Pastel theme.
// Pastels: sage #D4E0DA · peach #EBD8C8 · lavender #D9CFD9
// Fonts: .font-display (Playfair Display) · .font-body (Manrope) · .font-label (Outfit)

const AboutUs = () => {
  const navigate = useNavigate();
  const [emailCopied, setEmailCopied] = useState(false);

  const handleEmailClick = () => {
    const email = 'neha@teamnova.ai';
    navigator.clipboard.writeText(email).then(() => {
      setEmailCopied(true);
      toast.success(`Email copied: ${email}`, {
        description: 'You can now paste it in your email client',
      });
      setTimeout(() => setEmailCopied(false), 3000);
      window.location.href = `mailto:${email}`;
    }).catch(() => {
      toast.error('Could not copy email', { description: 'Please email: neha@teamnova.ai' });
    });
  };

  const values = [
    { icon: Lightbulb, tint: '#D4E0DA', title: 'Innovation First',  description: 'We leverage cutting-edge AI technology to transform complex compliance processes into simple, automated workflows.' },
    { icon: Users,     tint: '#EBD8C8', title: 'Customer Success',  description: 'Your success is our mission. We partner with you every step of the way to ensure you become audit-ready faster.' },
    { icon: Shield,    tint: '#D9CFD9', title: 'Trust & Security',  description: 'We treat your data with the highest standards of security and confidentiality, maintaining SOC 2 and GDPR compliance.' },
    { icon: Heart,     tint: '#D4E0DA', title: 'Empathy-Driven',    description: "We understand the pain of compliance. That's why we're committed to making it stress-free and accessible for everyone." },
  ];

  const approachItems = [
    { icon: Shield, tint: '#D4E0DA', title: 'Deep Compliance Knowledge', description: 'Our team brings extensive experience in SOC 2, GDPR, HIPAA, ISO 27001, and PCI DSS compliance from working with enterprises and startups alike.' },
    { icon: Zap,    tint: '#EBD8C8', title: 'AI-First Platform',         description: "We've built Nova from the ground up with AI at its core — automating evidence collection, monitoring, and compliance workflows that traditionally take months." },
    { icon: Users,  tint: '#D9CFD9', title: 'Customer-Centric',          description: 'Every feature, every workflow, and every interaction is designed with one goal: helping you become audit-ready faster and with less stress.' },
  ];

  const whyChoose = [
    'Proven compliance methodology backed by industry best practices and regulatory expertise',
    'AI automation that cuts compliance time by 50–70% compared to traditional approaches',
    'Transparent subscription pricing — independent auditor fees billed separately',
    'White-glove support from compliance experts who understand startup challenges',
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#050505] text-[#0A0A0A] dark:text-[#F5F5F5] font-body">
      <Header />

      {/* ===================== HERO ===================== */}
      <section
        className="bg-[#D4E0DA]/55 dark:bg-[#101F18]/55 pt-24 md:pt-32 pb-24 md:pb-32 px-6 border-b border-black/10 dark:border-white/10 min-h-[480px] md:min-h-[640px]"
        data-testid="about-hero"
      >
        <div className="max-w-5xl mx-auto text-center w-full">
          <p className="font-label text-[11px] uppercase tracking-[0.3em] text-black/55 dark:text-white/55 mb-8">
            About Nova
          </p>
          <h1 className="font-display font-semibold leading-[1.25] tracking-tight text-2xl sm:text-3xl lg:text-4xl mb-8">
            Making compliance simple,
            <br />
            <span className="italic text-black/85 dark:text-white/90">accessible, and automated.</span>
          </h1>
          <p className="font-body text-base md:text-lg text-black/70 dark:text-white/70 max-w-3xl mx-auto leading-relaxed mb-10">
            Nova is an AI-powered compliance and certification platform built to help organisations automate evidence collection, track remediation activities and prepare for independent certification.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/register')}
              data-testid="about-get-started"
              className="font-label bg-[#0F62FE] dark:bg-[#2E79FF] text-white px-8 py-4 rounded-full text-xs uppercase tracking-[0.2em] shadow-sm hover:bg-[#0A4FCC] dark:hover:bg-[#5C97FF] hover:shadow-md transition-all duration-300"
            >
              Start Free Trial
            </button>
            <button
              type="button"
              onClick={() => navigate('/pricing')}
              data-testid="about-view-pricing"
              className="font-label border border-[#0A0A0A]/20 dark:border-white/25 text-[#0A0A0A] dark:text-[#F5F5F5] px-8 py-4 rounded-full text-xs uppercase tracking-[0.2em] hover:border-[#0A0A0A] dark:hover:border-white/30 hover:bg-[#D9CFD9]/40 dark:hover:bg-[#1E1724]/40 transition-all duration-500"
            >
              View Pricing
            </button>
          </div>
        </div>
      </section>

      {/* ===================== OUR STORY ===================== */}
      <section className="px-6 py-24 md:py-28" data-testid="about-our-story">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.2fr_1fr] gap-12 lg:gap-16 items-start">
          <div>
            <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-4">
              Our Story
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mb-8 leading-tight">
              Born from a global challenge.
            </h2>
            <div className="space-y-5 text-base md:text-lg text-black/75 dark:text-white/75 leading-relaxed">
              <p>
                In today's interconnected world, <strong className="text-[#0A0A0A] dark:text-[#F5F5F5]">compliance has become mandatory</strong> — not optional. Whether you're handling customer data in California, serving users in Europe, or processing payments globally, regulations like GDPR, CCPA, HIPAA, SOC 2, and ISO 27001 are now the baseline for doing business.
              </p>
              <p>
                But here's the problem: <strong className="text-[#0A0A0A] dark:text-[#F5F5F5]">traditional compliance is broken</strong>. It takes 6–12 months, costs $50,000–$200,000+, requires hiring expensive consultants, and pulls your team away from building products.
              </p>
              <p>
                <strong className="text-[#0A0A0A] dark:text-[#F5F5F5]">Nova was born to change this.</strong> We believe every startup and growing company should have access to enterprise‑grade compliance — without the enterprise price tag, complexity, or timeline.
              </p>
              <p>
                Powered by <strong className="text-[#0A0A0A] dark:text-[#F5F5F5]">Inceptica LLC</strong> and based in the <strong className="text-[#0A0A0A] dark:text-[#F5F5F5]">United States</strong>, Nova serves companies across all markets with a singular focus: making compliance simple, fast, and accessible through AI automation.
              </p>
            </div>
          </div>

          <div className="rounded-2xl bg-[#EBD8C8]/45 dark:bg-[#2E1B10]/45 p-8 md:p-10 space-y-8">
            {[
              { icon: TrendingUp, tint: '#D4E0DA', stat: '3–6 months',     blurb: 'Average time to audit‑readiness vs 6–12 months traditionally.' },
              { icon: Zap,        tint: '#EBD8C8', stat: '80% automated', blurb: 'Evidence collection and compliance monitoring.' },
              { icon: Globe,      tint: '#D9CFD9', stat: 'Global coverage', blurb: 'SOC 2, GDPR, HIPAA, ISO 27001, PCI DSS, AI frameworks.' },
            ].map(({ icon: Icon, tint, stat, blurb }) => (
              <div key={stat} className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 nv-tint" style={{ backgroundColor: tint }}>
                  <Icon className="w-5 h-5 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-display text-xl md:text-2xl text-[#0A0A0A] dark:text-[#F5F5F5] leading-tight">{stat}</p>
                  <p className="text-sm md:text-base text-black/65 dark:text-white/65 leading-relaxed mt-1">{blurb}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FOUNDER ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="about-founder">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-3">
              Meet Our Founder
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mb-4 leading-tight">
              Led by an innovator in <span className="italic text-black/85 dark:text-white/90">AI &amp; Business Analytics.</span>
            </h2>
            <p className="font-body text-base md:text-lg text-black/65 dark:text-white/65 max-w-3xl mx-auto">
              Srineha Korugundla founded Nova with a vision to democratize compliance through artificial intelligence.
            </p>
          </div>

          <div className="rounded-2xl overflow-hidden border border-black/10 dark:border-white/10 grid lg:grid-cols-5">
            <div className="lg:col-span-2 bg-[#D4E0DA]/45 dark:bg-[#101F18]/45 flex items-center justify-center p-8 lg:p-12">
              <img
                src="/srineha-korugundla.jpg"
                alt="Srineha Korugundla, Founder of Nova"
                className="w-64 h-64 lg:w-80 lg:h-80 object-cover rounded-2xl"
              />
            </div>

            <div className="lg:col-span-3 p-8 lg:p-12 bg-[#FAFAFA] dark:bg-[#050505]">
              <h3 className="font-display text-2xl md:text-3xl text-[#0A0A0A] dark:text-[#F5F5F5] leading-tight">Srineha Korugundla</h3>
              <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mt-2 mb-6">Founder</p>

              <div className="space-y-4 text-sm md:text-base text-black/75 dark:text-white/75 leading-relaxed mb-7">
                <p>
                  Srineha is a visionary entrepreneur who bridges the worlds of <strong className="text-[#0A0A0A] dark:text-[#F5F5F5]">business strategy, customer success, and AI innovation</strong>. With a Master's degree in Business Analytics from the University of Rochester's Simon Business School and extensive experience in corporate and startup environments, she brings a unique perspective on solving complex business challenges through intelligent solutions.
                </p>
                <p>
                  Before founding Nova, Srineha honed her expertise in <strong className="text-[#0A0A0A] dark:text-[#F5F5F5]">data‑driven decision making and business transformation</strong> at Wells Fargo, FastCollab, and Inceptica, where she led initiatives that improved customer engagement, streamlined operations, and delivered measurable business impact.
                </p>
                <p>
                  <strong className="text-[#0A0A0A] dark:text-[#F5F5F5]">Deeply inspired by AI's potential</strong> to transform industries, Srineha founded Nova with a mission to democratize compliance. She inspires her team and customers with her belief that <strong className="text-[#0A0A0A] dark:text-[#F5F5F5]">AI can make the impossible possible</strong> — turning complex regulatory requirements into simple, automated workflows that empower companies to grow without barriers.
                </p>
                <blockquote className="border-l-2 border-[#EBD8C8] pl-5 italic font-display text-lg md:text-xl text-black/80 dark:text-white/85 leading-snug mt-2">
                  "Compliance shouldn't be a barrier to growth. With AI, we can turn it into a competitive advantage."
                </blockquote>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <a
                  href="https://www.linkedin.com/in/srinehakorugundla"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="founder-linkedin"
                  className="font-label uppercase tracking-[0.2em] text-[11px] bg-[#0A0A0A] dark:bg-[#141414] text-[#FAFAFA] hover:bg-[#EBD8C8] dark:hover:bg-[#2E1B10] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] px-5 py-3.5 rounded-full transition-colors duration-500 inline-flex items-center justify-center gap-2"
                >
                  <Linkedin className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Connect on LinkedIn
                </a>
                <button
                  type="button"
                  onClick={handleEmailClick}
                  data-testid="founder-email"
                  className="font-label uppercase tracking-[0.2em] text-[11px] border border-[#0A0A0A]/20 dark:border-white/25 text-[#0A0A0A] dark:text-[#F5F5F5] hover:border-[#0A0A0A] dark:hover:border-white/30 hover:bg-[#D4E0DA]/40 dark:hover:bg-[#101F18]/40 px-5 py-3.5 rounded-full transition-colors duration-500 inline-flex items-center justify-center gap-2"
                >
                  {emailCopied ? (
                    <Check className="w-3.5 h-3.5" strokeWidth={1.5} />
                  ) : (
                    <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />
                  )}
                  {emailCopied ? 'Email Copied' : 'Get in Touch'}
                </button>
              </div>

              <div className="pt-6 border-t border-black/10 dark:border-white/10">
                <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-4">
                  Background Highlights
                </p>
                <ul className="grid sm:grid-cols-2 gap-3 text-sm text-black/70 dark:text-white/70">
                  {[
                    { icon: Award,      label: 'MS, Business Analytics — University of Rochester' },
                    { icon: Rocket,     label: 'Co‑Founder at Inceptica' },
                    { icon: TrendingUp, label: 'Business Strategy & Analytics — Wells Fargo' },
                    { icon: Lightbulb,  label: 'AI‑Inspired Business Solutions Leader' },
                  ].map(({ icon: Icon, label }) => (
                    <li key={label} className="flex items-start gap-2">
                      <Icon className="w-4 h-4 mt-0.5 shrink-0 text-black/55 dark:text-white/55" strokeWidth={1.5} />
                      <span>{label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== VALUES (BENTO) ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="about-values">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-3">
              Our Values
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mb-4">
              What drives us every day
            </h2>
            <p className="font-body text-base md:text-lg text-black/65 dark:text-white/65 max-w-2xl mx-auto">
              Our values guide everything we do — from product development to customer support.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-black/10 dark:border-white/10">
            {values.map((value, i) => {
              const Icon = value.icon;
              return (
                <div
                  key={value.title}
                  data-testid={`about-value-${i}`}
                  className="group relative border-r border-b border-black/10 dark:border-white/10 p-8 md:p-10 transition-colors duration-700 flex flex-col"
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = value.tint + '40'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-8 nv-tint"
                    style={{ backgroundColor: value.tint + 'CC' }}
                  >
                    <Icon className="w-5 h-5 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
                  </div>
                  <p
                    className="font-display font-semibold leading-none text-5xl md:text-6xl mb-5 select-none"
                    style={{ color: value.tint }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </p>
                  <h3 className="font-display text-xl md:text-2xl leading-tight mb-3">
                    {value.title}
                  </h3>
                  <p className="font-body text-sm md:text-base text-black/65 dark:text-white/65 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== APPROACH ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="about-approach">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-3">
              Our Approach
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mb-4 leading-tight">
              Built on experience, focused on results.
            </h2>
            <p className="font-body text-base md:text-lg text-black/65 dark:text-white/65 max-w-3xl mx-auto">
              Nova combines years of compliance expertise with cutting‑edge AI technology to deliver results from day one.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12">
            {approachItems.map(({ icon: Icon, tint, title, description }, i) => (
              <div
                key={title}
                data-testid={`about-approach-${i}`}
                className="rounded-2xl border border-black/10 dark:border-white/10 p-8 md:p-10 hover:border-black/20 dark:hover:border-white/20 transition-colors duration-500 text-center"
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5 nv-tint"
                  style={{ backgroundColor: tint + 'CC' }}
                >
                  <Icon className="w-6 h-6 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-xl md:text-2xl leading-tight mb-3">{title}</h3>
                <p className="font-body text-sm md:text-base text-black/65 dark:text-white/65 leading-relaxed">{description}</p>
              </div>
            ))}
          </div>

          {/* Why Choose Nova */}
          <div className="rounded-2xl bg-[#D4E0DA]/45 dark:bg-[#101F18]/45 p-8 md:p-12">
            <div className="text-center mb-8">
              <h3 className="font-display font-semibold text-2xl md:text-3xl tracking-tight">
                Why companies choose Nova
              </h3>
            </div>
            <ul className="grid md:grid-cols-2 gap-x-10 gap-y-5 max-w-4xl mx-auto">
              {whyChoose.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Check className="w-4 h-4 mt-1.5 shrink-0 text-black/65 dark:text-white/65" strokeWidth={1.5} />
                  <span className="font-body text-base text-black/80 dark:text-white/85 leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ===================== FINAL CTA (INVERTED) ===================== */}
      <section
        className="bg-[#0A0A0A] dark:bg-[#141414] py-24 md:py-32 px-6 text-center"
        data-testid="about-cta-section"
      >
        <div className="max-w-4xl mx-auto">
          <p className="font-label text-[11px] uppercase tracking-[0.3em] text-white/65 mb-6">
            Begin
          </p>
          <h2 className="font-display font-semibold text-3xl md:text-4xl text-[#FAFAFA] mb-6 leading-[1.1] tracking-tight">
            Join us on this journey.
          </h2>
          <p className="font-body text-sm md:text-base text-white/70 mb-12 max-w-2xl mx-auto">
            Whether you're a startup looking to win your first enterprise deal or a growing company scaling globally, we're here to make compliance your competitive advantage.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/register')}
              data-testid="about-cta-start"
              className="font-label bg-[#FAFAFA] dark:bg-[#050505] text-[#0A0A0A] dark:text-[#F5F5F5] hover:bg-[#D4E0DA] dark:hover:bg-[#101F18] uppercase tracking-[0.2em] text-xs px-8 py-4 rounded-full transition-all duration-500"
            >
              Start Free Trial
            </button>
            <button
              type="button"
              onClick={() => navigate('/pricing')}
              data-testid="about-cta-pricing"
              className="font-label border border-white/25 bg-transparent text-[#FAFAFA] hover:bg-[#FAFAFA] dark:hover:bg-[#050505] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] uppercase tracking-[0.2em] text-xs px-8 py-4 rounded-full transition-all duration-500"
            >
              View Pricing Plans
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;
